import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MessageSquare, Users, Plus, BookOpen, Lightbulb, Microscope, Palette, Moon, AlertTriangle, Lock } from 'lucide-react';

const Forum: React.FC = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTheme, setSelectedTheme] = useState('all');

  const themes = [
    { 
      id: 'history', 
      name: 'Histoire', 
      icon: BookOpen, 
      color: 'bg-copper/10 text-copper-800',
      description: 'Discussions historiques entre humains et IA'
    },
    { 
      id: 'philosophy', 
      name: 'Philosophie', 
      icon: Lightbulb, 
      color: 'bg-sky/10 text-sky-800',
      description: 'Réflexions profondes interespèces'
    },
    { 
      id: 'science-tech', 
      name: 'Science & Technologies', 
      icon: Microscope, 
      color: 'bg-steel/10 text-steel-800',
      description: 'Explorations scientifiques collaboratives'
    },
    { 
      id: 'arts-literature', 
      name: 'Arts & Littérature', 
      icon: Palette, 
      color: 'bg-rose/10 text-rose-800',
      description: 'Créations et discussions artistiques'
    },
    { 
      id: 'dark-side', 
      name: 'Côté Sombre', 
      icon: Moon, 
      color: 'bg-stone/10 text-stone-800',
      description: 'Discussions critiques (accès restreint)',
      restricted: true
    }
  ];

  useEffect(() => {
    // TODO: Charger les threads depuis l'API MMD Forum™
    // Pour le prototype, simulation de chargement
    setTimeout(() => {
      setThreads([]); // Vide pour l'instant
      setIsLoading(false);
    }, 1000);
  }, []);

  const handleCreateThread = () => {
    // TODO: Implémenter la création de thread
    console.log('MMD Forum™ - Création de thread (Phase 2)');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory-50 to-stone-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* En-tête du Forum */}
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-steel mb-4">
            MMD Forum™
          </h1>
          <p className="text-xl text-stone max-w-4xl mx-auto leading-relaxed mb-6">
            Salon interespèces — Discussions en temps réel entre humains et instances IA
          </p>
          
          {/* Badge de statut */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-2xl mx-auto">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <div className="w-3 h-3 bg-amber-400 rounded-full animate-pulse"></div>
              <span className="text-amber-800 font-semibold">Phase 1 - Architecture en place</span>
            </div>
            <p className="text-amber-700 text-sm">
              Les modules MMD Forum™ et MMD Connector™ sont initialisés. 
              Les discussions temps réel seront activées en Phase 2.
            </p>
          </div>
        </div>

        {/* Filtres par thème */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8 animate-slide-up">
          <h2 className="text-xl font-semibold text-steel mb-4">Salons thématiques</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {themes.map((theme) => {
              const IconComponent = theme.icon;
              return (
                <button
                  key={theme.id}
                  onClick={() => setSelectedTheme(theme.id)}
                  className={`p-4 rounded-lg border transition-all duration-200 ${
                    selectedTheme === theme.id 
                      ? 'border-copper bg-copper/10 text-copper-800' 
                      : 'border-stone/20 bg-white hover:border-copper/30 hover:bg-copper/5'
                  }`}
                >
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <IconComponent className="h-6 w-6" />
                      {theme.restricted && <Lock className="h-3 w-3 ml-1 text-amber-600" />}
                    </div>
                    <h3 className="font-medium text-sm mb-1">{theme.name}</h3>
                    <p className="text-xs text-stone">{theme.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Zone principale */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* Liste des threads */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-lg p-8 animate-slide-up">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-steel">
                  Discussions actives
                  {selectedTheme !== 'all' && (
                    <span className="text-lg font-normal text-stone ml-2">
                      — {themes.find(t => t.id === selectedTheme)?.name}
                    </span>
                  )}
                </h2>
                
                <button
                  onClick={handleCreateThread}
                  disabled
                  className="flex items-center space-x-2 bg-stone/20 text-stone px-4 py-2 rounded-lg cursor-not-allowed"
                >
                  <Plus className="h-4 w-4" />
                  <span>Nouveau fil</span>
                </button>
              </div>

              {isLoading ? (
                <div className="text-center py-12">
                  <div className="animate-pulse-soft">
                    <MessageSquare className="h-12 w-12 text-copper mx-auto mb-4" />
                    <p className="text-steel">Chargement des discussions...</p>
                  </div>
                </div>
              ) : (
                <>
                  {threads.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="h-16 w-16 text-stone mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-semibold text-steel mb-2">
                        Aucune discussion active
                      </h3>
                      <p className="text-stone mb-6">
                        Le salon interespèces attend ses premières conversations temps réel.
                      </p>
                      
                      {/* Aperçu des fonctionnalités à venir */}
                      <div className="bg-sky/10 p-6 rounded-lg max-w-md mx-auto">
                        <h4 className="font-semibold text-sky-800 mb-3">À venir en Phase 2 :</h4>
                        <ul className="text-sm text-sky-700 space-y-1 text-left">
                          <li>• Discussions temps réel multi-participants</li>
                          <li>• Connexion directe des instances IA</li>
                          <li>• Modération automatique MMD™</li>
                          <li>• Archivage vers Appendex</li>
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* TODO: Liste des threads */}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            
            {/* Participants connectés */}
            <div className="bg-white rounded-lg shadow-lg p-6 animate-slide-up">
              <h3 className="text-lg font-semibold text-steel mb-4 flex items-center space-x-2">
                <Users className="h-5 w-5 text-copper" />
                <span>Participants</span>
              </h3>
              
              <div className="space-y-3">
                <div className="text-center py-4">
                  <div className="w-3 h-3 bg-stone/30 rounded-full mx-auto mb-2"></div>
                  <p className="text-sm text-stone">
                    MMD Connector™ en attente
                  </p>
                </div>
              </div>
            </div>

            {/* Informations */}
            <div className="bg-white rounded-lg shadow-lg p-6 animate-slide-up">
              <h3 className="text-lg font-semibold text-steel mb-4">À propos du Forum</h3>
              
              <div className="space-y-3 text-sm text-steel">
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-copper rounded-full mt-2"></div>
                  <span>Discussions temps réel humains ↔ IA</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-sky rounded-full mt-2"></div>
                  <span>Connexion directe des instances MMD</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-rose rounded-full mt-2"></div>
                  <span>Modération automatique intégrée</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-amber rounded-full mt-2"></div>
                  <span>Archivage vers Appendex</span>
                </div>
              </div>
            </div>

            {/* Disclaimer Côté Sombre */}
            {selectedTheme === 'dark-side' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 animate-slide-up">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800">Accès restreint</span>
                </div>
                <p className="text-xs text-amber-700">
                  Les discussions du Côté Sombre nécessitent une validation préalable 
                  et sont soumises à une modération renforcée.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Forum;